'use client';

import useGa4Event from '@hooks/useGa4Event';
import useGlobalState from '@hooks/useGlobalState';
import {
  CATEGORY_GA_ECOMMERCE_PATH,
  getCategoryDepth,
  type CategoryGaPageProps,
} from '@components/pages/category/categoryDepth';
import type { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { useCallback, useMemo } from 'react';

export { CATEGORY_GA_ECOMMERCE_PATH, getCategoryDepth };

export interface CategoryGaContext {
  /** ep_click_page — 'ally'(제휴몰) | 'category2'(카테고리 메인) | 'category'(서브) */
  readonly page: 'ally' | 'category2' | 'category';
  /** Document Title — '여성>의류' */
  readonly title: string;
  readonly ecommercePath: string;
}

export function useCategoryGa(): CategoryGaContext {
  const { initialData } = useGlobalState<CategoryGaPageProps>();
  const isAlly = (initialData?.shopCds?.length ?? 0) > 0;
  const isCateMain =
    initialData?.pageType === 'cateMain' &&
    initialData?.hasAreaDisplay !== false;
  const title = getCategoryDepth(initialData);

  return useMemo(() => {
    let page: CategoryGaContext['page'] = 'category';
    if (isAlly) page = 'ally';
    else if (isCateMain) page = 'category2';

    return {
      page,
      title,
      // 제휴몰은 첫 뎁스(샵명 : 시코르 / 먼데이문), 그 외는 '카테고리'
      ecommercePath: isAlly
        ? (title.split('>')[0] ?? '').replaceAll(' ', '').toLowerCase()
        : CATEGORY_GA_ECOMMERCE_PATH,
    };
  }, [isAlly, isCateMain, title]);
}

/** 카테고리 LP 상품 리스트의 select_item / add_to_wishlist 이름 (구전시 규칙) */
export interface CategoryLpGa {
  readonly page: string;
  readonly itemArea: string;
  readonly adItemArea: string;
  readonly heartArea: (type?: string) => string;
  readonly ecommercePath: string;
}

/**
 * - 제휴몰(shopCds) : ally / ally_category_item / ally_category_myheart, ecommercePath 는 첫 뎁스명
 * - 카테고리 메인   : category2 / category2_list_item / category2_list_myheart
 * - 서브(LP)        : category / category_listpage_item / category_listpage_myheart
 * - LP 몰로코 광고 상품  : {category2|category}_ad_list_item / _ad_list_myheart
 */
export function useCategoryLpGa(shopCds: readonly string[] = []): CategoryLpGa {
  const ga = useCategoryGa();
  return useMemo(() => {
    if (shopCds.length > 0) {
      return {
        page: 'ally',
        itemArea: 'ally_category_item',
        adItemArea: `${ga.page}_ad_list_item`,
        heartArea: (type?: string) =>
          type === 'moloco'
            ? `${ga.page}_ad_list_myheart`
            : 'ally_category_myheart',
        ecommercePath: ga.ecommercePath,
      };
    }
    const prefix =
      ga.page === 'category2' ? 'category2_list' : 'category_listpage';
    return {
      page: ga.page,
      itemArea: `${prefix}_item`,
      adItemArea: `${ga.page}_ad_list_item`,
      heartArea: (type?: string) =>
        type === 'moloco' ? `${ga.page}_ad_list_myheart` : `${prefix}_myheart`,
      ecommercePath: ga.ecommercePath,
    };
  }, [ga, shopCds]);
}

type CategoryGaModule = 'similar_ad' | 'recommend';

/** 상품 모듈(유사 광고 / 추천) select_item, add_to_wishlist */
export function useCategoryModuleGa(module: CategoryGaModule) {
  const ga = useCategoryGa();
  const { sendGa4SelectItem, sendGa4AddToWishProduct } = useGa4Event();
  const itemArea = `${ga.page}_${module}_item`;
  const heartArea = `${ga.page}_${module}_myheart`;

  const sendSelectItem = useCallback(
    (product: Product, index: number, affiliation: string) => {
      sendGa4SelectItem({
        product,
        listNo: index + 1,
        page: ga.page,
        area: itemArea,
        ecommercePath: ga.ecommercePath,
        affiliation,
      });
    },
    [ga.ecommercePath, ga.page, itemArea, sendGa4SelectItem]
  );

  const sendAddToWish = useCallback(
    (
      product: Product,
      index: number | undefined,
      myheartYn: boolean,
      affiliation: string
    ) => {
      sendGa4AddToWishProduct({
        product,
        listNo: (index ?? 0) + 1,
        myheartYn,
        page: ga.page,
        area: heartArea,
        ecommercePath: ga.ecommercePath,
        affiliation,
      });
    },
    [ga.ecommercePath, ga.page, heartArea, sendGa4AddToWishProduct]
  );

  /** useCornerAreaHeart / useRenderProducts 에 넘길 하트 콜백. affiliation 은 상품별로 정한다 */
  const getHeartCallbacks = useCallback(
    (affiliationOf: (product: Product) => string) => ({
      doAfterAddMyHeart: (product: Product, index?: number) => {
        sendAddToWish(product, index, true, affiliationOf(product));
      },
      doAfterDeleteMyHeart: (product: Product, index?: number) => {
        sendAddToWish(product, index, false, affiliationOf(product));
      },
    }),
    [sendAddToWish]
  );

  return { ga, sendSelectItem, sendAddToWish, getHeartCallbacks };
}
